'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from '@/ui-components/atoms/Button/Button';
import { EntityListItem, EntityListItemProps, Variant } from '@/ui-components/molecules/EntityListItem/EntityListItem';
import { ImagePlaceholder } from '@/ui-components/atoms/ImagePlaceholder/ImagePlaceholder';
import styles from './searchResultList.module.scss';

export interface SearchResultListProps {
    items: EntityListItemProps[];
    itemsPerPage?: number;
    id?: string;
}

export function SearchResultList({ items, itemsPerPage = 7, id }: SearchResultListProps) {
    const [visibleItemsCount, setVisibleItemsCount] = useState(itemsPerPage);
    const firstNewItemRef = useRef<HTMLAnchorElement | null>(null);
    const hasLoadedMore = useRef<boolean>(false);

    const handleLoadMore = useCallback(() => {
        hasLoadedMore.current = true;
        setVisibleItemsCount((prevCount) => prevCount + itemsPerPage);
    }, [itemsPerPage]);

    useEffect(() => {
        if (hasLoadedMore.current && firstNewItemRef.current) {
            requestAnimationFrame(() => {
                if (firstNewItemRef.current) {
                    firstNewItemRef.current.focus();
                }
                hasLoadedMore.current = false;
            });
        }
    }, [visibleItemsCount]);

    const visibleItems = useMemo(() => {
        return items.slice(0, visibleItemsCount).map((item, i) => {
            const isFirstNewItem = i === visibleItemsCount - itemsPerPage;
            return (
                <li key={i}>
                    <EntityListItem
                        {...item}
                        image={item.image || <ImagePlaceholder />}
                        variant={Variant.small}
                        ref={isFirstNewItem ? firstNewItemRef : null}
                    />
                </li>
            );
        });
    }, [items, visibleItemsCount, itemsPerPage]);

    return (
        <>
            <ul className={styles.searchResultList} id={id}>
                {visibleItems}
            </ul>
            {items.length > visibleItemsCount && (
                <Button
                    type="button"
                    onClick={handleLoadMore}
                    variant="secondary"
                    fitting="hug-contents"
                    className={styles.loadMoreButton}
                    aria-label="Load more search results"
                >
                    Load more
                </Button>
            )}
        </>
    );
}
